import { useFormikContext } from 'formik';
import { Product, ProductWithPlanFields } from 'types/product';
import { useCallback, useRef } from 'react';
import useUomConvert from 'hooks/useUomConvert';
import { Maths } from 'helpers/maths';
import {
  calculateProductValuesFromRate,
  calculateProductValuesFromTotal,
  calculateProductValuesFromTank
} from '../../../helpers/calculateProduct';
import {
  calculateAppliedAcrePercent,
  calculateAppliedAcres,
  validateAppliedPercent
} from '../helpers/calculateAcreage';
import { QUANTITY_MODES } from '../constants';
import { sanitizeNumericString } from '../helpers/sanitizeNumericString';

/**
 * Custom hook to handle ONLY product value calculations on blur events
 */
// eslint-disable-next-line import/prefer-default-export
export const useProductCalculations = ({
  tankCount,
  isTankInfoEnabled
}: {
  tankCount?: number;
  isTankInfoEnabled?: boolean;
} = {}) => {
  const { values, setFieldValue } = useFormikContext<ProductWithPlanFields>();
  const convertFn = useUomConvert();

  /**
   * Always read the latest form values and tank settings through refs. The
   * number inputs commit their calculations through a debounced blur, so a blur
   * scheduled before a UOM change (or tank-info toggle) can fire afterwards.
   * Reading from stale closures there would recompute with old units/settings
   * and clobber the values already written by the change handler.
   * recalculateAfterPriceChange is also memoized with [] deps (stable identity
   * for ProductCostSection's useEffect), so it must read everything via refs.
   */
  const productToAddRef = useRef(values);
  productToAddRef.current = values;

  const tankCountRef = useRef(tankCount);
  tankCountRef.current = tankCount;

  const isTankInfoEnabledRef = useRef(isTankInfoEnabled);
  isTankInfoEnabledRef.current = isTankInfoEnabled;

  const convertFnRef = useRef(convertFn);
  convertFnRef.current = convertFn;

  // Rate per Area
  const onRatePerAcreCalculation = (
    newValue: number = 0,
    recalculatedAppliedArea = 0,
    changedUnit?: string,
    product?: Product
  ) => {
    const productToAdd = productToAddRef.current;
    const { ratePerAreaUnit, appliedAreaValue = 0 } = productToAdd;
    const currentTankCount = tankCountRef.current;
    const currentIsTankInfoEnabled = isTankInfoEnabledRef.current;

    const result = calculateProductValuesFromRate(
      sanitizeNumericString(newValue),
      recalculatedAppliedArea || appliedAreaValue,
      changedUnit || ratePerAreaUnit,
      product || productToAdd,
      currentIsTankInfoEnabled && currentTankCount
        ? { tankInformation: { tankCount: currentTankCount } }
        : {},
      convertFnRef.current
    );

    if (result) {
      setFieldValue('appliedAreaValue', result.appliedAreaValue);
      setFieldValue('totalCost', result.totalCostAtTimeOfCreation);

      setFieldValue('totalProduct', result.totalProductValue);
      setFieldValue('ratePerTank', result.ratePerTankValue);
    }
    return result;
  };

  // Total Product
  const onTotalProductCalculation = (
    newValue: number = 0,
    recalculatedAppliedArea = 0,
    changedUnit?: string,
    product?: Product
  ) => {
    const productToAdd = productToAddRef.current;
    const { totalProductUnit, appliedAreaValue = 0 } = productToAdd;
    const currentTankCount = tankCountRef.current;
    const currentIsTankInfoEnabled = isTankInfoEnabledRef.current;

    const result = calculateProductValuesFromTotal(
      sanitizeNumericString(newValue),
      recalculatedAppliedArea || appliedAreaValue,
      changedUnit || totalProductUnit,
      product || productToAdd,
      currentIsTankInfoEnabled && currentTankCount
        ? { tankInformation: { tankCount: currentTankCount } }
        : {},
      convertFnRef.current
    );

    if (result) {
      setFieldValue('appliedAreaValue', result.appliedAreaValue);
      setFieldValue('totalCost', result.totalCostAtTimeOfCreation);

      setFieldValue('ratePerAcre', result.ratePerAreaValue);
      setFieldValue('ratePerTank', result.ratePerTankValue);
    }

    return result;
  };

  const onRatePerTankCalculation = (
    newValue: number = 0,
    recalculatedAppliedArea = 0,
    changedUnit?: string,
    product?: Product
  ) => {
    const productToAdd = productToAddRef.current;
    const { ratePerTankUnit, appliedAreaValue = 0 } = productToAdd;
    const currentTankCount = tankCountRef.current;
    const currentIsTankInfoEnabled = isTankInfoEnabledRef.current;

    const result = calculateProductValuesFromTank(
      sanitizeNumericString(newValue),
      recalculatedAppliedArea || appliedAreaValue,
      changedUnit || ratePerTankUnit || '',
      product || productToAdd,
      currentIsTankInfoEnabled && currentTankCount
        ? { tankInformation: { tankCount: currentTankCount } }
        : {},
      convertFnRef.current
    );

    if (result) {
      setFieldValue('appliedAreaValue', result.appliedAreaValue);
      setFieldValue('totalCost', result.totalCostAtTimeOfCreation);

      setFieldValue('totalProduct', result.totalProductValue);
      setFieldValue('ratePerAcre', result.ratePerAreaValue);
    }

    return result;
  };

  // Mirrors legacy AddProductPlanModal: multiply the single-application
  // rate*area totals by count rather than back-solving via
  // onTotalProductCalculation, so ratePerAcre stays the true per-app rate.
  const onApplicationCountCalculation = (numberOfApps: number = 1) => {
    const productToAdd = productToAddRef.current;
    const {
      ratePerAcre = 0,
      ratePerAreaUnit,
      appliedAreaValue = 0
    } = productToAdd;
    const currentTankCount = tankCountRef.current;
    const currentIsTankInfoEnabled = isTankInfoEnabledRef.current;

    const base = calculateProductValuesFromRate(
      sanitizeNumericString(ratePerAcre),
      appliedAreaValue,
      ratePerAreaUnit,
      productToAdd,
      currentIsTankInfoEnabled && currentTankCount
        ? { tankInformation: { tankCount: currentTankCount } }
        : {},
      convertFnRef.current
    );

    if (!base) return base;

    const totalProductValue = Maths.multiplyRound(
      base.totalProductValue,
      numberOfApps,
      2
    );
    const totalCostAtTimeOfCreation = Maths.multiplyRound(
      base.totalCostAtTimeOfCreation,
      numberOfApps,
      2
    );

    setFieldValue('applicationCount', numberOfApps);
    setFieldValue('totalProduct', totalProductValue);
    setFieldValue('totalCost', totalCostAtTimeOfCreation);

    return { ...base, totalProductValue, totalCostAtTimeOfCreation };
  };

  const recalculateAfterAreaUpdate = (appliedArea = 0) => {
    const productToAdd = productToAddRef.current;
    // just to re-trigger calculation after an area update
    if (productToAdd.quantityMode === QUANTITY_MODES.TOTAL_PRODUCT) {
      onTotalProductCalculation(productToAdd?.totalProduct || 0, appliedArea);
    }

    if (productToAdd.quantityMode === QUANTITY_MODES.RATE_PER_ACRE) {
      onRatePerAcreCalculation(productToAdd?.ratePerAcre || 0, appliedArea);
    }

    if (productToAdd.quantityMode === QUANTITY_MODES.RATE_PER_TANK) {
      onRatePerTankCalculation(productToAdd?.ratePerTank || 0, appliedArea);
    }
  };

  const recalculateAfterPriceChange = useCallback(() => {
    const productToAdd = productToAddRef.current;
    // just to re-trigger calculation after a price change
    let result;
    if (productToAdd.quantityMode === QUANTITY_MODES.TOTAL_PRODUCT) {
      result = onTotalProductCalculation(
        productToAdd?.totalProduct || 0,
        productToAdd.appliedAreaValue
      );
    } else if (productToAdd.quantityMode === QUANTITY_MODES.RATE_PER_TANK) {
      result = onRatePerTankCalculation(
        productToAdd?.ratePerTank || 0,
        productToAdd.appliedAreaValue
      );
    } else {
      // Rate per area is the default mode
      result = onRatePerAcreCalculation(
        productToAdd?.ratePerAcre || 0,
        productToAdd.appliedAreaValue
      );
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Applied Area
  const onAppliedAreaCalculation = (newArea: number | string = 0) => {
    const productToAdd = productToAddRef.current;
    const area = sanitizeNumericString(newArea);
    if (area > productToAdd.totalAppliedAcreage) {
      setTimeout(() => {
        setFieldValue('appliedAreaValue', productToAdd.totalAppliedAcreage);
        setFieldValue('coveragePercent', 100);
      }, 0);
      return;
    }

    const percentage = calculateAppliedAcrePercent(
      area,
      productToAdd.totalAppliedAcreage
    );

    setFieldValue('coveragePercent', percentage);
    recalculateAfterAreaUpdate(area);
  };

  const onAppliedPercentageCalculation = (
    newPercentage: string | number = 0
  ) => {
    const productToAdd = productToAddRef.current;
    let percentage = sanitizeNumericString(newPercentage);

    const totalAppliedAcreage = productToAdd.totalAppliedAcreage || 0;

    if (percentage > 100) {
      percentage = 100;
      setFieldValue('coveragePercent', 100);
    }

    if (validateAppliedPercent(percentage)) {
      const calculatedAcres = calculateAppliedAcres(
        percentage,
        totalAppliedAcreage
      );
      setFieldValue('appliedAreaValue', calculatedAcres);
      recalculateAfterAreaUpdate(calculatedAcres);
    } else {
      // reset back to previous acreage if percentage is invalid
      onAppliedAreaCalculation(productToAdd.appliedAreaValue);
    }
  };

  return {
    onRatePerAcreCalculation,
    onTotalProductCalculation,
    onRatePerTankCalculation,
    onApplicationCountCalculation,

    recalculateAfterPriceChange,
    onAppliedAreaCalculation,
    onAppliedPercentageCalculation
  };
};
